import React, {useEffect, useState} from "react";
import {Box, Typography} from "@material-ui/core";
import {useForm} from "react-hook-form";
import FfairModal from "view/components/modal/FfairModal";
import FfairPaper from "view/components/FfairPaper";
import Form, {ActionButton, Actions, Checkbox} from "view/components/Form";
import parsePath from "parse-filepath";
import {TaskReviewIcon} from "view/images/navigationIcons";
import {blue} from "@material-ui/core/colors";
import AddIcon from "@material-ui/icons/Add";
import {RawAttachmentFile} from "ts/api/attachmentFile";

import RichTextEditor from "view/components/RichTextEditor";
import {nanoid} from "nanoid";
import {parseTextFromHTML} from "tools/file/parseTextFromHTML";
import ReactionAttachments from "view/components/TasksList/components/Modals/ReactionAttachments";
import SignTaskModal from "view/components/TasksList/components/Modals/ApprovalModal/SignTaskModal";
import {apiV2} from "api/firebase";
import {invalidateTaskTimelineQueries} from "api/taskTimelineCache";
import parseURLParams from "view/EmailTaskReview/parseURLParams";
import {
  IBaseFile,
  ICustomField,
  ShowDateFormat,
  SignatureType,
  TaskApprovalType,
  TaskMultipleReviewersMode,
} from "@evennt-tech/api-v2-core";
import {
  IAnonymousShowProjection,
  IVerifyTaskEmailReviewRes,
  ICreateSignatureReq,
} from "@evennt-tech/api-v2-external";
import {ICreateSignaturePublicReq} from "view/components/TasksList/components/Modals/ApprovalModal/SignTaskModal/types";
import {v4 as uuid} from "uuid";
import {TEN_MB_IN_BYTES} from "tools/file/bytesToMegaBytes";
import {hasOversizedFile} from "tools/file/fileSizeLimit";
import buildSignatureUploadFile from "tools/file/buildSignatureUploadFile";
import {
  SIGNATURE_ACCEPTED_TYPES,
  validateFileBeforeUpload,
} from "tools/file/fileValidation/fileValidation";

import {usePublicSignatures} from "store/signature/signature-public.store";
import {multipleSubmit, singleSubmit} from "./submit";
import {useStyles} from "./styles";

/**
 * Anonymous email-reviewer approve / reject modal.
 */
type ApprovalModalProps = {
  approval: boolean;
  onClose: () => void;
  // `expectPdf` = an approval, which triggers async Task PDF generation the
  // page should poll for. A rejection produces no PDF.
  onSubmit?: (expectPdf: boolean) => void;
  taskReview: IVerifyTaskEmailReviewRes<string>;
  show: IAnonymousShowProjection<string>;
  customFields: ICustomField[];
  email: string;
  dateFormat: ShowDateFormat;
  classes?: {
    root?: string;
  };
};

type ApprovalMessageForm = {
  message: string;
  allowResubmitAfterApproval: boolean;
};

const ApprovalModal: React.FC<ApprovalModalProps> = ({
  approval,
  onClose,
  onSubmit: onSubmitAction,
  taskReview,
  show,
  email,
  classes: propsClasses,
}) => {
  const {key, reviewer} = parseURLParams();
  const [isSignatureModalOpen, setIsSignatureModalOpen] = useState<boolean>(
    false
  );
  const [selectedSignatureId, setSelectedSignatureId] = useState<
    string | undefined
  >(undefined);

  const {createPublic} = usePublicSignatures();

  const [files, setFiles] = useState<RawAttachmentFile[]>([]);
  const classes = useStyles();

  const {task, reviewers, company, stand} = taskReview;
  const approvalType = task.approvalConfig?.type ?? null;
  const isMultiReviewer = approvalType === TaskApprovalType.MULTIPLE_REVIEWERS;
  const isEmailReview = approvalType === TaskApprovalType.EMAIL;
  const isSequential =
    task.approvalConfig?.multipleReviewersMode ===
    TaskMultipleReviewersMode.SEQUENTIAL;

  const form = useForm<ApprovalMessageForm>({
    mode: "all",
    defaultValues: {message: ""},
  });

  const {register, setValue, handleSubmit, trigger} = form;
  const taskInfo = company
    ? `${task.title} by ${company.name ?? ""}`
    : `${task.title}`;

  // `allowResubmitAfterApproval` MUST be read out of the form here: the
  // checkbox below is registered but its value was previously dropped, so
  // unticking it silently did nothing (the instance kept the column's
  // `DEFAULT true` and the exhibitor could still re-open an approved task).
  // Defaults to true to match the checkbox's `defaultChecked`.
  const onSubmit = handleSubmit(
    async ({message, allowResubmitAfterApproval}) => {
      // Library-pick signature — same shape both ApprovalModal organiser
      // paths use. Server-side `writeSignature` copies the library row
      // into `task_signatures` keyed by the reviewer's email (no
      // organiser id on the email-reviewer path).
      const signature =
        selectedSignatureId && approval
          ? {
              type: SignatureType.TEXT,
              signatureId: selectedSignatureId,
            }
          : null;
      // Decision target — the shared per-email HMAC key alone is
      // ambiguous when the reviewer sits on several tasks; pin the
      // verdict to the exact instance this card rendered (UUIDs from
      // the verify response, same row the BE resolved for display).
      const decisionTarget = {
        taskId: task.id,
        standId: stand.id,
      };
      // Only carried on an approval — a reject / revision hands the task back to
      // the exhibitor regardless of this toggle.
      const resubmitAfterApproval = approval
        ? allowResubmitAfterApproval ?? true
        : undefined;
      if (isMultiReviewer) {
        await multipleSubmit({
          showId: show.id,
          key: key!,
          ...decisionTarget,
          approval,
          message,
          organiserReactionFiles: files,
          signature,
          allowResubmitAfterApproval: resubmitAfterApproval,
        });
      } else {
        await singleSubmit({
          showId: show.id,
          key: key!,
          ...decisionTarget,
          approval,
          message,
          files,
          signature,
          allowResubmitAfterApproval: resubmitAfterApproval,
        });
      }

      invalidateTaskTimelineQueries({
        showId: show.id,
        standId: stand.id,
        taskId: task.id,
      });

      onClose();
      if (onSubmitAction) {
        // Only an approval produces a Task PDF worth polling for.
        onSubmitAction(approval);
      }
    }
  );

  useEffect(() => {
    register("message", {
      required: !approval,
      validate: (value) => {
        if (approval) {
          return true;
        }
        if (parseTextFromHTML(value).length === 0 && value?.length !== 0) {
          return false;
        }
      },
    });
  }, [approval, register]);

  const onUploadFiles = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files?.length) {
      const uploadedFiles = Array.from(event.target.files).map((file) => ({
        id: nanoid(),
        title: parsePath(file.name).name,
        file,
      }));
      setFiles((prev) => (prev ? [...prev, ...uploadedFiles] : uploadedFiles));
    }
  };
  const handleCreateSignature = async (data: ICreateSignaturePublicReq) => {
    try {
      const {name, role, type, shouldBeShownInList, text, file, showId} = data;

      const trimmedName = name.trim();
      const trimmedRole = role.trim();
      const title = `${trimmedName.replace(/\s/g, "-")}-signature`;

      const signatureFile = file.file;

      const {isValid, errorMessage} = validateFileBeforeUpload(signatureFile, {
        sizeLimit: TEN_MB_IN_BYTES,
        acceptedTypes: SIGNATURE_ACCEPTED_TYPES,
      });

      if (!isValid) {
        console.error(errorMessage);
        return;
      }

      // PDFs are flattened to a PNG (first page); images pass through as-is.
      const fileToUpload = await buildSignatureUploadFile(signatureFile, title);

      // The visitor has no organiser session, so always use the public
      // storage + signature endpoints.
      if (!key) return;

      // Prefer the plaintext reviewer email from the verify response;
      // the `reviewer` URL param is absent on magic-links.
      const reviewerEmail = taskReview.reviewerEmail || reviewer || "";

      const {token} = await apiV2.signature.signatureToFile.public.init({
        showId,
        key,
        reviewer: reviewerEmail,
      });

      const responseStorageService = await apiV2.fileUpload.uploadFile({
        file: fileToUpload,
        token,
        requestId: uuid(),
      });

      if (!responseStorageService?.token) return;

      const signaturePublicReq: ICreateSignatureReq = {
        name: trimmedName,
        role: trimmedRole,
        type,
        shouldBeShownInList,
        showId,
        token: responseStorageService.token,
        ...(type === SignatureType.TEXT && {text}),
      };

      return await createPublic({
        ...signaturePublicReq,
        key,
        reviewer: reviewerEmail,
      });
    } catch (e) {
      console.error("Failed to create signature:", e);
    }
  };

  const onChangeMessage = (message: string) => {
    setValue("message", message, {
      shouldDirty: true,
      shouldValidate: true,
    });
    trigger();
  };
  const handleSignatureSubmit = (id?: string) => {
    if (id) {
      setSelectedSignatureId(id);
    }
  };
  const handleOpenSignatureModal = () => {
    setIsSignatureModalOpen(true);
    setSelectedSignatureId(undefined);
  };

  const {isValid, isSubmitting} = form.formState;

  // Fallback to email prop when reviewer param is absent.
  const currentReviewerEmail = email || reviewer;
  const isSignatureRequired =
    !!task.approvalConfig?.isSignatureRequired &&
    !selectedSignatureId &&
    (isEmailReview ||
      (isMultiReviewer &&
        reviewers.some(
          (r) => !!r.reviewerEmail && r.reviewerEmail === currentReviewerEmail
        )));
  // An over-limit file stays in the list (so the user can see + remove it) but
  // blocks submit and surfaces the inline error below.
  const hasOversizedAttachment = hasOversizedFile(files);
  const isSubmitDisabled =
    !isValid || isSubmitting || isSignatureRequired || hasOversizedAttachment;

  return (
    <>
      <FfairModal open onClose={onClose} className={propsClasses?.root}>
        <FfairPaper className={classes.root} style={{width: "580px"}}>
          <Typography className={classes.taskInfo}>{taskInfo}</Typography>
          <Typography className={classes.heading}>
            {approval ? "Approve" : "Reject"} Task
          </Typography>
          <Form
            form={form}
            onSubmit={onSubmit}
            style={{
              padding: "24px 0 0 0",
            }}
          >
            <RichTextEditor
              id="message"
              label="Message..."
              onChange={onChangeMessage}
            />
            <ReactionAttachments
              inputId="approve-reject-attachment"
              files={files}
              onUpload={onUploadFiles}
              onRemove={(id) =>
                setFiles((prev) => prev.filter((file) => file.id !== id))
              }
            />
            {/* Resubmission toggle only makes sense for an APPROVAL — a
                rejected task goes back to the exhibitor anyway. */}
            {approval && (
              <Box pt={2}>
                <Checkbox
                  defaultChecked
                  name="allowResubmitAfterApproval"
                  label="Allow exhibitor to resubmit after approval"
                  color="primary"
                />
              </Box>
            )}
            <Actions className={classes.modalActionButtons}>
              <Box>
                {task.approvalConfig?.isSignatureRequired && (
                  <ActionButton
                    startIcon={
                      selectedSignatureId ? (
                        <TaskReviewIcon style={{color: blue[500]}} />
                      ) : (
                        <AddIcon />
                      )
                    }
                    color="primary"
                    text="Add signature"
                    onClick={handleOpenSignatureModal}
                    disabled={isSubmitting}
                  />
                )}
              </Box>
              <Box>
                <ActionButton
                  color="primary"
                  text="Cancel"
                  onClick={async () => {
                    onClose();
                    setFiles([]);
                  }}
                />
                <ActionButton
                  className={classes.submitButton}
                  disabled={isSubmitDisabled}
                  onClick={onSubmit}
                  color="primary"
                  variant="contained"
                  text="Submit"
                />
              </Box>
            </Actions>
          </Form>
          {isSignatureModalOpen && (
            <SignTaskModal
              signatures={[]}
              showId={show.id}
              createSignature={(params) => handleCreateSignature(params)}
              onClose={() => setIsSignatureModalOpen(false)}
              onSubmit={handleSignatureSubmit}
              taskTitle="Task title"
              viewModes={{new: true}}
            />
          )}
        </FfairPaper>
      </FfairModal>
    </>
  );
};

export default ApprovalModal;
